/**
 * The page that opens one entity by id -- an account, a post, a profile -- in
 * both of its states: nothing chosen yet (the picker card of Figma `299:10749`)
 * and one chosen (the page itself). The switcher sits in the same header row in
 * both, so a reader changes entity from where they already are -- or, for a page
 * passing `embedded`, inside the page's own header through `EmbeddedSwitcher`.
 */

import { Check, ChevronDown, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Skeleton } from '../../ui/skeleton';
import { useBreadcrumbLeaf } from '../shell';
import { initials } from '../shell/initials';
import { et } from './messages';

export interface EntityOption {
  id: string;
  name: string;
  /** A handle or a short second line; null when there is none. */
  detail: string | null;
  imageUrl: string | null;
  /** A post's thumbnail is square; an account's avatar is round. */
  square?: boolean;
  /** Drawn before the detail, e.g. the platform mark. */
  mark?: ReactNode;
}

/** The list as its query stands: rows, still loading, or failed. */
export type EntityOptions = EntityOption[] | 'loading' | 'failed';

export interface EntityLabels {
  /** "Selecionar conta": the trigger and the last crumb with nothing chosen. */
  select: string;
  /** "Escolha uma conta para ver": the picker card's heading. */
  title: string;
  search: string;
  /** Nothing matched the search, or the list is empty. */
  none: string;
}

const RECENT_LIMIT = 5;

function recentKey(scope: string): string {
  return `entity-recent:${scope}`;
}

function readRecent(scope: string): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(recentKey(scope)) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/** The ids this viewer opened last in `scope`, newest first. Per browser only:
 *  losing it costs the reader an ordering, never an entity. */
function useRecent(scope: string, openedId: string | null): string[] {
  const [recent, setRecent] = useState(() => readRecent(scope));
  useEffect(() => {
    if (openedId === null) return;
    const next = [openedId, ...readRecent(scope).filter((id) => id !== openedId)].slice(
      0,
      RECENT_LIMIT,
    );
    setRecent(next);
    try {
      localStorage.setItem(recentKey(scope), JSON.stringify(next));
    } catch {
      // Storage refused (private window, quota): the list simply stays unordered.
    }
  }, [scope, openedId]);
  return recent;
}

function OptionAvatar(props: { option: EntityOption }): ReactNode {
  const shape = props.option.square === true ? 'rounded-md' : undefined;
  return (
    <Avatar size="sm" className={shape} aria-hidden>
      {props.option.imageUrl !== null && (
        <AvatarImage src={props.option.imageUrl} alt="" className="object-cover" />
      )}
      <AvatarFallback className={shape}>
        {initials(props.option.name.replace(/^@/, ''))}
      </AvatarFallback>
    </Avatar>
  );
}

/** One entity as a one-line row: avatar, mark, name, then its detail muted beside the name.
 *  Any entity list or picker draws its rows with this. */
export function EntityRow(props: {
  option: EntityOption;
  selected?: boolean;
  trailing?: ReactNode;
}): ReactNode {
  return (
    <>
      <OptionAvatar option={props.option} />
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        {props.option.mark !== undefined && (
          <span className="flex shrink-0 text-muted-foreground">{props.option.mark}</span>
        )}
        <span className="min-w-0 truncate text-foreground">{props.option.name}</span>
        {props.option.detail !== null && (
          <span className="min-w-0 shrink-2 truncate text-caption text-muted-foreground">
            {props.option.detail}
          </span>
        )}
      </span>
      {props.selected === true ? <Check aria-hidden className="size-4" /> : props.trailing}
    </>
  );
}

function EntityCommand(props: {
  options: EntityOptions;
  selectedId: string | null;
  recent: string[];
  labels: EntityLabels;
  onSelect: (id: string) => void;
  inline: boolean;
}): ReactNode {
  const options = Array.isArray(props.options) ? props.options : [];
  const byId = new Map(options.map((option) => [option.id, option]));
  const recent = props.recent.flatMap((id) => byId.get(id) ?? []);
  const recentIds = new Set(recent.map((option) => option.id));
  const rest = options.filter((option) => !recentIds.has(option.id));
  const trailing = props.inline ? (
    <ChevronRight aria-hidden className="size-4 text-muted-foreground" />
  ) : null;

  const item = (option: EntityOption) => (
    <CommandItem
      key={option.id}
      value={option.id}
      keywords={[option.name, option.detail ?? '']}
      onSelect={() => props.onSelect(option.id)}
      className="gap-3"
    >
      <EntityRow option={option} selected={option.id === props.selectedId} trailing={trailing} />
    </CommandItem>
  );

  return (
    <Command
      label={props.labels.search}
      className={props.inline ? 'command-input-framed gap-2 bg-transparent' : undefined}
    >
      <CommandInput placeholder={props.labels.search} />
      <CommandList className={props.inline ? 'max-h-80' : undefined}>
        {props.options === 'failed' ? (
          <p role="alert" className="py-6 text-center text-sm text-muted-foreground">
            {et('failed')}
          </p>
        ) : props.options === 'loading' ? (
          <div className="flex flex-col gap-2 p-2" aria-busy="true" aria-label={et('loading')}>
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex items-center gap-3">
                <Skeleton className="size-7 rounded-full" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <CommandEmpty>{props.labels.none}</CommandEmpty>
            {recent.length > 0 && (
              <>
                <CommandGroup heading={et('recent')}>{recent.map(item)}</CommandGroup>
                {rest.length > 0 && <CommandSeparator />}
              </>
            )}
            {rest.length > 0 && <CommandGroup>{rest.map(item)}</CommandGroup>}
          </>
        )}
      </CommandList>
    </Command>
  );
}

interface SwitcherProps {
  options: EntityOptions;
  selectedId: string | null;
  recent: string[];
  labels: EntityLabels;
  onSelect: (id: string) => void;
  /** Replaces the default trigger's content, e.g. a page header's own avatar and name. */
  trigger?: ReactNode;
}

/** The header control: the current entity, or "Selecionar …", opening the list. */
export function EntitySwitcher(props: SwitcherProps): ReactNode {
  const [open, setOpen] = useState(false);
  const current =
    (Array.isArray(props.options) ? props.options : []).find(
      (option) => option.id === props.selectedId,
    ) ?? null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {props.trigger !== undefined ? (
          // Named by its content, not an `aria-label`: it sits in the page's heading, whose
          // accessible name must stay the entity's own name.
          <button
            type="button"
            aria-expanded={open}
            aria-haspopup="listbox"
            className="group hover:bg-accent focus-visible:ring-ring -my-1.5 flex max-w-full min-w-0 items-center gap-2 rounded-md p-1.5 text-left outline-none focus-visible:ring-2"
          >
            {props.trigger}
            <ChevronDown
              aria-hidden
              className="text-muted-foreground group-hover:text-foreground size-4 shrink-0"
            />
          </button>
        ) : (
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            // A combobox takes no name from its content.
            aria-label={
              current === null ? props.labels.select : `${props.labels.select}: ${current.name}`
            }
            className="h-auto min-h-9 w-full justify-start gap-3 py-1.5 font-normal sm:w-80"
          >
            {current === null ? (
              <span className="flex-1 truncate text-left text-muted-foreground">
                {props.labels.select}
              </span>
            ) : (
              <span className="flex min-w-0 flex-1 items-center gap-3 text-left">
                <EntityRow option={current} />
              </span>
            )}
            <ChevronsUpDown aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-72 p-0">
        <EntityCommand
          {...props}
          inline={false}
          onSelect={(id) => {
            setOpen(false);
            props.onSelect(id);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

const EmbeddedContext = createContext<Omit<SwitcherProps, 'trigger'> | null>(null);

/** The switcher for a page rendered under `EntityPage embedded`, drawn with `children` as its
 *  trigger so the page's own header opens the list. */
export function EmbeddedSwitcher(props: { children: ReactNode }): ReactNode {
  const shared = useContext(EmbeddedContext);
  if (shared === null) return props.children;
  return <EntitySwitcher {...shared} trigger={props.children} />;
}

/**
 * One instance page, in both states. `selectedId` null draws the picker card alone, never
 * a second picker above it (and `placeholder` under it, the frame's skeleton of the page to come) and
 * names "Selecionar …" as the last crumb; otherwise `children` is the page, and
 * names its own crumb.
 */
export function EntityPage(props: {
  /** Keys the viewer's recent list, e.g. `account:<campaignId>`. */
  scope: string;
  options: EntityOptions;
  selectedId: string | null;
  labels: EntityLabels;
  onSelect: (id: string) => void;
  placeholder?: ReactNode;
  children?: ReactNode;
  /** The page draws the switcher in its own header (`EmbeddedSwitcher`) once one is chosen. */
  embedded?: boolean;
}): ReactNode {
  const recent = useRecent(props.scope, props.selectedId);
  useBreadcrumbLeaf(props.selectedId === null ? props.labels.select : undefined);
  const shared = {
    options: props.options,
    selectedId: props.selectedId,
    recent,
    labels: props.labels,
    onSelect: props.onSelect,
  };
  if (props.selectedId !== null && props.embedded === true) {
    return <EmbeddedContext value={shared}>{props.children}</EmbeddedContext>;
  }
  return (
    <div className="flex w-full flex-col gap-6">
      {props.selectedId !== null ? (
        <>
          <div className="flex">
            <EntitySwitcher {...shared} />
          </div>
          {props.children}
        </>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>
                <h2 className="text-body font-semibold">{props.labels.title}</h2>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <EntityCommand {...shared} inline />
            </CardContent>
          </Card>
          {props.placeholder}
        </>
      )}
    </div>
  );
}
